import * as React from 'react';
import { ThemeProvider, useTheme as useNextTheme } from 'next-themes';

/** Chakra has no color-mode system, so this wraps `next-themes`: a `light`/`dark` class on the document
 * root drives the semantic tokens' `_dark` condition. Provides the provider and the useColorMode hooks. */

export type ColorMode = 'light' | 'dark';

type ColorModeProviderProps = React.ComponentProps<typeof ThemeProvider>;

export function ColorModeProvider(props: ColorModeProviderProps) {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} disableTransitionOnChange {...props} />
  );
}

export interface UseColorModeReturn {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, setTheme, forcedTheme } = useNextTheme();
  const colorMode = (forcedTheme || resolvedTheme || 'light') as ColorMode;
  return {
    colorMode,
    setColorMode: setTheme,
    toggleColorMode: () => setTheme(colorMode === 'dark' ? 'light' : 'dark'),
  };
}

export function useColorModeValue<TLight, TDark>(light: TLight, dark: TDark): TLight | TDark {
  const { colorMode } = useColorMode();
  return colorMode === 'dark' ? dark : light;
}
